import { Link } from "react-router-dom";
import {
  ArrowRight, Calculator, ClipboardCheck, LayoutGrid, ShieldCheck,
  Sliders, FileText, MapPin, CheckCircle2, Clock,
} from "lucide-react";

const STREAMS_PREVIEW = [
  "Ontario (OINP)", "British Columbia (BC PNP)", "Alberta (AAIP)", "Saskatchewan (SINP)",
  "Manitoba (MPNP)", "Nova Scotia (NSNP)", "New Brunswick", "Prince Edward Island",
];

const FEATURES = [
  { icon: Calculator, title: "PNP Score Calculator", desc: "Enter your profile once and get estimated points for provincial grids (BC SIRS, SINP, MPNP) plus CRS for Express Entry-aligned streams." },
  { icon: ClipboardCheck, title: "Eligibility Checker", desc: "See Eligible, Potentially Eligible or Not Eligible for each stream — with the exact criteria met and what's missing." },
  { icon: LayoutGrid, title: "Stream Matrix", desc: "Compare every supported stream side by side: province, score, cutoff, status and your standing at a glance." },
  { icon: Sliders, title: "Rules Engine", desc: "Program criteria, point tables, cutoffs and stream status live in an editable configuration — kept current as programs change." },
];

const MODES = [
  {
    to: "/assessment", testId: "mode-eligibility-card", kicker: "Path 1", icon: ClipboardCheck,
    title: "Check my eligibility", cta: "Start eligibility check",
    desc: "Fill in your complete profile once and see where you stand across every supported stream in 8 provinces.",
    points: ["Eligible / Potentially / Not Eligible per stream", "Criteria met and what's missing", "CRS estimate + per-stream points"],
  },
  {
    to: "/calculator", testId: "mode-calculator-card", kicker: "Path 2", icon: Calculator, dark: true,
    title: "Calculate score for a stream", cta: "Pick a stream",
    desc: "Already know the stream you're targeting? Pick it first and answer only the questions that stream actually scores.",
    points: ["Shorter, stream-specific questionnaire", "Score vs cutoff with full point breakdown", "Focused criteria checklist for that stream"],
  },
];

const STEPS = [
  { n: "01", title: "Build your profile", desc: "Age, education, language, work experience, connections and job offer — one structured questionnaire." },
  { n: "02", title: "Calculate & check", desc: "We estimate your points per stream and evaluate eligibility instantly in your browser." },
  { n: "03", title: "Compare pathways", desc: "Review breakdowns, compare streams and understand exactly what strengthens each application." },
];

export default function Home() {
  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#0F172A] text-white">
        <div className="absolute inset-0 hero-grid opacity-40" />
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1507992781348-310259076fe0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A] via-[#0F172A]/90 to-[#0F172A]/40" />
        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="max-w-3xl animate-fade-up">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-slate-200">
              <span className="flex h-2 w-2 rounded-full bg-[#C8102E]" />
              Canadian Provincial Nominee Program Assessment
            </div>
            <h1 className="mt-6 font-heading text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl lg:text-6xl">
              Find the PNP pathway <span className="text-[#F87171]">built for your profile.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-300">
              Enter your immigration profile once. PR Pathfinder calculates your estimated provincial
              points and CRS, then checks your eligibility across every major Canadian PNP stream — clearly,
              privately, and with no account required.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/assessment"
                data-testid="hero-start-btn"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-[#C8102E] px-6 py-3.5 text-base font-semibold text-white shadow-lg transition-all hover:bg-[#A60C24] hover:shadow-xl"
              >
                Check my eligibility <ArrowRight className="h-5 w-5" />
              </Link>
              <Link
                to="/calculator"
                data-testid="hero-stream-calculator-btn"
                className="inline-flex items-center justify-center gap-2 rounded-md border border-white/20 bg-white/5 px-6 py-3.5 text-base font-semibold text-white transition-colors hover:bg-white/10"
              >
                <Calculator className="h-5 w-5" /> Calculate score for a stream
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-400">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-emerald-400" /> No login</span>
              <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4 text-emerald-400" /> ~5 minute profile</span>
              <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4 text-emerald-400" /> 8 provinces · 22 streams</span>
            </div>
          </div>
        </div>
      </section>

      {/* Province ticker */}
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-slate-400">Supported programs</p>
          <div className="flex flex-wrap gap-2">
            {STREAMS_PREVIEW.map((s) => (
              <span key={s} className="rounded-full border border-slate-200 bg-slate-50 px-3.5 py-1.5 text-sm font-medium text-slate-700">
                {s}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Two paths */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24" data-testid="mode-choice-section">
        <div className="max-w-2xl">
          <h2 className="font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Choose how you want to start
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            Scan every province at once, or go deep on the one stream you're targeting.
          </p>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-2">
          {MODES.map((m) => (
            <Link key={m.to} to={m.to} data-testid={m.testId}
              className={`group relative flex flex-col overflow-hidden rounded-2xl border p-8 shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg ${m.dark ? "border-[#0F172A] bg-[#0F172A] text-white" : "border-slate-200 bg-white text-slate-900"}`}>
              <span className={`text-xs font-bold uppercase tracking-widest ${m.dark ? "text-[#F87171]" : "text-[#C8102E]"}`}>{m.kicker}</span>
              <div className={`mt-5 flex h-12 w-12 items-center justify-center rounded-lg ${m.dark ? "bg-white/10 text-white" : "bg-[#FEF2F2] text-[#C8102E]"}`}>
                <m.icon className="h-6 w-6" />
              </div>
              <h3 className="mt-5 font-heading text-2xl font-bold">{m.title}</h3>
              <p className={`mt-3 leading-relaxed ${m.dark ? "text-slate-300" : "text-slate-600"}`}>{m.desc}</p>
              <ul className={`mt-5 space-y-1.5 text-sm ${m.dark ? "text-slate-300" : "text-slate-600"}`}>
                {m.points.map((p) => (
                  <li key={p} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> {p}</li>
                ))}
              </ul>
              <span className={`mt-8 inline-flex items-center gap-2 font-semibold ${m.dark ? "text-white" : "text-[#C8102E]"}`}>
                {m.cta} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8 lg:pb-24">
        <div className="max-w-2xl">
          <h2 className="font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Two tools, one profile
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            A precise scoring engine and a transparent eligibility checker, working from the same structured questionnaire.
          </p>
        </div>
        <div className="mt-12 grid gap-6 md:grid-cols-2">
          {FEATURES.map((f) => (
            <div key={f.title} className="group rounded-xl border border-slate-200 bg-white p-8 shadow-sm transition-all hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-[#FEF2F2] text-[#C8102E]">
                <f.icon className="h-6 w-6" />
              </div>
              <h3 className="mt-5 font-heading text-xl font-semibold text-slate-900">{f.title}</h3>
              <p className="mt-2 leading-relaxed text-slate-600">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-white py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">How it works</h2>
          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {STEPS.map((s) => (
              <div key={s.n} className="relative rounded-xl border border-slate-200 bg-slate-50/60 p-8">
                <span className="font-mono-tech text-4xl font-bold text-[#C8102E]/25">{s.n}</span>
                <h3 className="mt-3 font-heading text-xl font-semibold text-slate-900">{s.title}</h3>
                <p className="mt-2 text-slate-600">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 flex justify-center">
            <Link
              to="/assessment"
              className="inline-flex items-center gap-2 rounded-md bg-[#C8102E] px-6 py-3.5 text-base font-semibold text-white shadow-sm transition-colors hover:bg-[#A60C24]"
            >
              Begin your assessment <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-6 rounded-2xl border border-slate-200 bg-white p-8 shadow-sm md:grid-cols-3 md:p-10">
          {[
            { icon: ShieldCheck, title: "Private by design", desc: "Your profile stays in your browser. Assessments run without creating an account and are not saved on our servers." },
            { icon: FileText, title: "Transparent methodology", desc: "Every score shows its breakdown, and every eligibility result explains the criteria met and missing." },
            { icon: CheckCircle2, title: "Kept current", desc: "Streams, thresholds and status are managed through an editable rules configuration as programs evolve." },
          ].map((t) => (
            <div key={t.title} className="flex gap-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[#0F172A] text-white">
                <t.icon className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-slate-900">{t.title}</h3>
                <p className="mt-1 text-sm leading-relaxed text-slate-600">{t.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
